import React, { useState } from "react";
import axios from "axios";
import { toast } from "../../utils/toast";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { errorMessage } from "../../utils/errorMessage";
import { signOut } from "../../utils/signOut";
import { API_DETAILS } from "../../App";

/**
 * Closing your own Youro account.
 *
 * Deliberately quiet: a line of small print at the foot of Membership that has
 * to be opened, then a confirmation that has to be typed. Nobody closes an
 * account by mis-tapping, and nobody hunting for it has to ask us to do it.
 *
 * What it does and does not remove is stated before the patient confirms, not
 * after: sign-in and contact details go, the medical record stays for as long
 * as the law requires it to be kept. See AccountClosureService on the server.
 */
const CloseAccountPanel = () => {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [, guard] = useSubmitGuard();

  const typedCorrectly = confirmation.trim().toUpperCase() === "CLOSE";

  const closeAccount = guard(async () => {
    if (!typedCorrectly) return;
    const url = API_DETAILS.baseUrl + API_DETAILS.PORT + API_DETAILS.baseExtension + "/account/close";
    try {
      await axios.post(url, {});
      toast.success("Your account is closed.");
      // The session is already revoked server-side; this clears what the browser
      // still holds and returns to the homepage.
      await signOut("/");
    } catch (err) {
      toast.error(errorMessage(err, "We couldn't close your account just now. Please try again, or email support@yourohealth.com."));
    }
  });

  return (
    <div className="close-account">
      {!open ? (
        <button type="button" className="close-account-link" onClick={() => setOpen(true)}>
          Close your account
        </button>
      ) : (
        <div className="close-account-panel" role="group" aria-label="Close your account">
          <h3 className="close-account-heading">Close your account</h3>
          <p className="close-account-text">
            You won't be able to sign in again, and we'll remove your email address, phone number and
            postal address. Any membership is set not to renew, and nothing further is charged.
          </p>
          <p className="close-account-text">
            Your medical records — visits, care plans, prescriptions and symptom scores — are kept for
            as long as the law requires, along with the name and date of birth that identify them.
            This cannot be undone; you'd need to sign up again to use Youro.
          </p>
          <label className="close-account-label" htmlFor="close-account-confirm">
            Type CLOSE to confirm
          </label>
          <input
            id="close-account-confirm"
            className="input-field input-border close-account-input"
            type="text"
            autoComplete="off"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
          <div className="close-account-actions">
            <button type="button" className="btn-filled" onClick={() => { setOpen(false); setConfirmation(""); }}>
              Keep my account
            </button>
            <button
              type="button"
              className="close-account-confirm"
              disabled={!typedCorrectly}
              onClick={closeAccount}
            >
              Close my account
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloseAccountPanel;
